import { createFileRoute, isRedirect, Outlet, redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { FeedbackOnboardingDialog } from "@/components/game/FeedbackOnboardingDialog";
import { BetaNoticeDialog } from "@/components/game/BetaNoticeDialog";
import { DiagnosticBoundary } from "@/components/DiagnosticBoundary";


const INVALID_TOKEN_RE = /invalid token|jwt|expired/i;

// Wait briefly for the Supabase client to hydrate the session from storage
// after OAuth redirects. Without this, the gate races the broker and bounces
// a freshly-signed-in user back to /auth.
async function waitForSession(maxMs = 1500) {
  const start = Date.now();
  let { data } = await supabase.auth.getSession();
  if (data.session?.user) return data.session;
  while (Date.now() - start < maxMs) {
    await new Promise((r) => setTimeout(r, 100));
    ({ data } = await supabase.auth.getSession());
    if (data.session?.user) return data.session;
  }
  return null;
}

export const Route = createFileRoute("/_app")({
  ssr: false,
  beforeLoad: async () => {
    try {
      const session = await waitForSession();
      if (!session?.user) throw redirect({ to: "/auth" });

      try {
        const { data, error } = await supabase.auth.getUser();
        if (data.user) return { user: data.user };

        if (error && INVALID_TOKEN_RE.test(error.message)) {
          await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
          throw redirect({ to: "/auth" });
        }

        return { user: session.user };
      } catch (error) {
        if (isRedirect(error)) throw error;

        const message = error instanceof Error ? error.message : "";
        if (INVALID_TOKEN_RE.test(message)) {
          await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
          throw redirect({ to: "/auth" });
        }

        return { user: session.user };
      }
    } catch (err) {
      if (isRedirect(err)) throw err;
      throw redirect({ to: "/auth" });
    }
  },
  component: () => (
    <DiagnosticBoundary>
      <Outlet />
      <FeedbackOnboardingDialog />
      <BetaNoticeDialog />
    </DiagnosticBoundary>
  ),

});

